import React, { useEffect, useRef, useState, useCallback } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "ckeditor-hashure-build/ckeditor";
import VideoSelector from "utils/VideoSelector";
import { useAuth } from "store/auth";

const CkEditorFullNew = ({ form, field, label }) => {
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const isFetchingRef = useRef(false);
  const observer = useRef();
  const { getToken } = useAuth();
  const [content, setContent] = useState(field?.value ?? "");
  const [isReady, setIsReady] = useState(false);

  const [editorImages, setEditorImages] = useState([]);
  const [videoImages, setVideoImages] = useState([]);

  const [editorPage, setEditorPage] = useState(1);
  const [videoPage, setVideoPage] = useState(1);
  const [editorHasMore, setEditorHasMore] = useState(true);
  const [videoHasMore, setVideoHasMore] = useState(true);
  const [editorSearchInput, setEditorSearchInput] = useState("");
  const [videoSearchInput, setVideoSearchInput] = useState("");

  const [showGalleryForEditor, setShowGalleryForEditor] = useState(false);
  const [showGalleryForVideo, setShowGalleryForVideo] = useState(false);
  const toolbarRef = useRef(null);
  const [customButton, setCustomButton] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [editorNoResults, setEditorNoResults] = useState(false);
  const [videoNoResults, setVideoNoResults] = useState(false);
  const [editorResetPage, setEditorResetPage] = useState(false);
  const [videoResetPage, setVideoResetPage] = useState(false);

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videos, setVideos] = useState([]);
  const [showSelect, setShowSelect] = useState(false);

  const [tempVideos, setTempVideos] = useState([{ video: null }]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(null);

  const OUR_DOMAIN = process.env.REACT_APP_MEDIA_BASE_URL;
  const formRef = useRef(form);

  useEffect(() => {
    setIsReady(true);
  }, []);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const formatVideosForBackend = (videosArray) => {
    console.log("🔧 [FORMAT] داده‌های ورودی برای فرمت‌دهی:", videosArray);

    const formatted = videosArray
      .map((videoObj, index) => {
        console.log(`🔧 [FORMAT] پردازش ویدیو ${index + 1}:`, videoObj);

        if (!videoObj.video || !videoObj.image) {
          console.warn(
            `❌ [FORMAT] ویدیو ${index + 1} حذف شد: video یا image وجود ندارد`
          );
          return null;
        }

        const videoSlug = videoObj.video.slug;
        const imageId = videoObj.image.id;

        if (!videoSlug || typeof videoSlug !== "string") {
          console.error(
            `❌ [FORMAT] ویدیو ${index + 1} حذف شد: videoSlug نامعتبر`,
            videoSlug
          );
          return null;
        }

        if (!imageId) {
          console.error(
            `❌ [FORMAT] ویدیو ${index + 1} حذف شد: image.id نامعتبر`,
            videoObj.image
          );
          return null;
        }

        const result = {
          image: imageId.toString(),
          slug: videoSlug,
          title:
            videoObj.image?.alt ||
            videoObj.video?.title ||
            `ویدیو ${index + 1}`,
        };

        console.log(`✅ [FORMAT] ویدیو ${index + 1} فرمت شد:`, result);
        return result;
      })
      .filter((item) => {
        const isValid = item !== null && item.image && item.slug && item.title;

        if (!isValid) {
          console.warn(`❌ [FORMAT] آیتم نامعتبر حذف شد:`, item);
        }
        return isValid;
      });

    console.log("🎯 [FORMAT] نتیجه نهایی فرمت‌دهی:", formatted);
    console.log("📊 [FORMAT] تعداد ویدیوهای معتبر:", formatted.length);

    return formatted;
  };

  useEffect(() => {
    if (formRef.current && formRef.current.setFieldValue) {
      try {
        const formattedVideos = formatVideosForBackend(videos);
        console.log("ارسال داده‌های ویدیوها به بک‌اند:", formattedVideos);

        if (Array.isArray(formattedVideos)) {
          formRef.current.setFieldValue("videos", formattedVideos);
          console.log("✅ داده‌ها با موفقیت به فرم ارسال شدند");
        } else {
          console.error("❌ فرمت ویدیوها معتبر نیست:", formattedVideos);
          formRef.current.setFieldValue("videos", []);
        }
      } catch (error) {
        console.error("❌ خطا در ارسال داده‌های ویدیوها:", error);
        formRef.current.setFieldValue("videos", []);
      }
    }
  }, [videos]);

  useEffect(() => {
    if (field?.value !== undefined && field?.value !== content) {
      setContent(field.value);
    }
  }, [field?.value]);

  const handleModelChange = (newContent) => {
    setContent(newContent);
    if (form && field?.name) {
      form.setFieldValue(field.name, newContent);
    }
  };

  function useDebounce(callback, delay) {
    const timeoutRef = useRef(null);

    const debouncedCallback = useCallback(
      (...args) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          callback(...args);
        }, delay);
      },
      [callback, delay]
    );

    useEffect(() => {
      return () => clearTimeout(timeoutRef.current);
    }, []);

    return debouncedCallback;
  }

  const debouncedEditorSearch = useDebounce((value) => {
    setEditorResetPage(true);
    setEditorPage(1);
  }, 800);

  const debouncedVideoSearch = useDebounce((value) => {
    setVideoResetPage(true);
    setVideoPage(1);
  }, 800);

  const handleEditorSearchChange = (e) => {
    const value = e.target.value;
    setEditorSearchInput(value);
    debouncedEditorSearch(value);
  };

  const handleVideoSearchChange = (e) => {
    const value = e.target.value;
    setVideoSearchInput(value);
    debouncedVideoSearch(value);
  };

  const fetchEditorImages = async (pageNumber = 1) => {
    try {
      setEditorLoading(true);
      setEditorNoResults(false);
      const baseURL = editorSearchInput
        ? `${process.env.REACT_APP_API_BASE_URL}/media-panel?page=${pageNumber}&search.fields=translations.title&search.value=${editorSearchInput}`
        : `${process.env.REACT_APP_API_BASE_URL}/media-panel?page=${pageNumber}`;

      const response = await fetch(baseURL, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const data = await response.json();

      if (data?.data?.length > 0) {
        const newImages = data?.data?.map((item, index) => ({
          url: item?.image.url.startsWith("http")
            ? item?.image.url
            : `${process.env.REACT_APP_MEDIA_BASE_URL}${item.image.url}`,
          title: item.translations?.fa?.title || "general",
          id: item.id || item.uuid || `${item.id}_${index}`,
        }));

        if (pageNumber === 1) {
          setEditorImages(newImages);
        } else {
          setEditorImages((prev) => [...prev, ...newImages]);
        }
        setEditorHasMore(true);
      } else {
        setEditorHasMore(false);
        if (pageNumber === 1) {
          setEditorImages([]);
          setEditorNoResults(true);
        }
      }
    } catch (error) {
      console.error("خطا در دریافت تصاویر ادیتور:", error);
    } finally {
      setEditorLoading(false);
    }
  };

  const fetchVideoImages = async (pageNumber = 1) => {
    try {
      setVideoLoading(true);
      setVideoNoResults(false);
      const baseURL = videoSearchInput
        ? `${process.env.REACT_APP_API_BASE_URL}/media-panel?page=${pageNumber}&search.fields=translations.title&search.value=${videoSearchInput}`
        : `${process.env.REACT_APP_API_BASE_URL}/media-panel?page=${pageNumber}`;

      const response = await fetch(baseURL, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const data = await response.json();

      if (data?.data?.length > 0) {
        const newImages = data?.data?.map((item, index) => ({
          url: item?.image.url.startsWith("http")
            ? item?.image.url
            : `${process.env.REACT_APP_MEDIA_BASE_URL}${item.image.url}`,
          title: item.translations?.fa?.title || "general",
          id: item.id || item.uuid || `${item.id}_${index}`,
        }));

        if (pageNumber === 1) {
          setVideoImages(newImages);
        } else {
          setVideoImages((prev) => [...prev, ...newImages]);
        }
        setVideoHasMore(true);
      } else {
        setVideoHasMore(false);
        if (pageNumber === 1) {
          setVideoImages([]);
          setVideoNoResults(true);
        }
      }
    } catch (error) {
      console.error("خطا در دریافت تصاویر ویدیو:", error);
    } finally {
      setVideoLoading(false);
    }
  };

  useEffect(() => {
    if (editorResetPage && editorPage === 1) {
      fetchEditorImages(1);
      setEditorResetPage(false);
    } else if (showGalleryForEditor) {
      fetchEditorImages(editorPage);
    }
  }, [editorPage, editorSearchInput, showGalleryForEditor]);

  useEffect(() => {
    if (videoResetPage && videoPage === 1) {
      fetchVideoImages(1);
      setVideoResetPage(false);
    } else if (showGalleryForVideo) {
      fetchVideoImages(videoPage);
    }
  }, [videoPage, videoSearchInput, showGalleryForVideo]);

  const lastEditorImageRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          editorHasMore &&
          !isFetchingRef.current
        ) {
          isFetchingRef.current = true;
          setEditorPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [editorHasMore]
  );

  const lastVideoImageRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          videoHasMore &&
          !isFetchingRef.current
        ) {
          isFetchingRef.current = true;
          setVideoPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [videoHasMore]
  );

  useEffect(() => {
    if (!editorLoading && !videoLoading) {
      isFetchingRef.current = false;
    }
  }, [editorLoading, videoLoading]);

  const uploadImageToServer = async (file) => {
    try {
      const title = form?.values?.translations?.fa?.title || "بدون عنوان";
      const formData = new FormData();
      formData.append("image", file);
      formData.append("video", null);
      formData.append("audio", null);
      formData.append("translations.fa.title", title);
      formData.append("type", "News");

      const token = getToken();
      if (!token) throw new Error("توکن احراز هویت وجود ندارد!");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/media-panel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "خطا در آپلود تصویر");
      }

      const responseData = await response.json();
      const imageUrl = responseData.data?.image?.url
        ? `${process.env.REACT_APP_MEDIA_BASE_URL}${responseData.data.image.url}`
        : null;

      if (!imageUrl) throw new Error("آدرس تصویر نامعتبر است");

      const finalUrl = imageUrl.startsWith("/")
        ? `${process.env.REACT_APP_MEDIA_BASE_URL}${imageUrl}`
        : imageUrl;

      return finalUrl;
    } catch (error) {
      console.error("خطا در آپلود تصویر:", error);
      throw error;
    }
  };

  const removeExternalLinksFromPastedContent = (html) => {
    if (!html) return html;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const paragraphs = doc.querySelectorAll("p");
      paragraphs.forEach((p) => {
        if (!p.innerHTML.trim()) {
          p.innerHTML = "&nbsp;";
        }
      });

      const links = doc.querySelectorAll("a[href]");
      links.forEach((link) => {
        const href = link.getAttribute("href");

        const isInternalLink =
          href.includes(OUR_DOMAIN) ||
          href.startsWith("/") ||
          href.startsWith("#") ||
          href.startsWith("mailto:") ||
          href.startsWith("tel:");

        if (!isInternalLink) {
          const span = doc.createElement("span");

          Array.from(link.attributes).forEach((attr) => {
            if (attr.name !== "href") {
              span.setAttribute(attr.name, attr.value);
            }
          });

          span.innerHTML = link.innerHTML;
          link.parentNode.replaceChild(span, link);
        }
      });

      const serializer = new XMLSerializer();
      let result = serializer.serializeToString(doc.body);

      result = result.replace(/<p><\/p>/g, "<p>&nbsp;</p>");
      result = result.replace(/<\/?body>/g, "");

      return result;
    } catch (error) {
      console.error("Error processing pasted content:", error);
      return html;
    }
  };

  const insertVideoAsHtml = useCallback((videoData, videoImage = null) => {
    console.log("درج ویدیو به صورت HTML:", videoData, videoImage);

    const editor = editorRef.current;
    if (!editor) {
      console.error("ویرایشگر آماده نیست");
      return;
    }

    const videoTitle = videoData.title || "ویدیو";
    const videoSlug = videoData.fullSlug;
    const thumbnailUrl = videoImage?.url || videoData.thumbnail || "";

    const videoHtml = `
      <div class="video-embed" data-video-slug="${videoSlug}" data-video-title="${videoTitle}">
        <div style="background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; padding: 16px; margin: 16px 0; max-width: 600px;">
          <div style="display: flex; align-items: flex-start; gap: 16px;">
            <div style="position: relative; flex-shrink: 0; width: 160px; height: 120px;">
              ${
                thumbnailUrl
                  ? `
                    <img 
                      src="${thumbnailUrl}" 
                      alt="${videoImage?.alt || videoTitle}" 
                      style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px; border: 1px solid #e2e8f0;"
                    />
                    <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.3); border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer;">
                      <div style="background: rgba(59, 130, 246, 0.9); width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: all 0.2s;">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                  `
                  : `
                    <div style="width: 100%; height: 100%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 8px; display: flex; align-items: center; justify-content: center; border: 1px solid #e2e8f0;">
                      <div style="background: rgba(255,255,255,0.9); width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="#3b82f6">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                  `
              }
            </div>
            
            <div style="flex: 1; min-width: 0;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="background: #3b82f6; color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; white-space: nowrap;">
                  VIDEO
                </span>
                <span style="font-weight: bold; color: #1e293b; font-size: 16px; word-break: break-word;">
                  ${videoTitle}
                </span>
              </div>
              
              <div style="background: white; padding: 12px; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 8px;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                  <span style="color: #64748b; font-size: 12px;">شناسه:</span>
                  <span style="color: #3b82f6; font-weight: bold; font-family: monospace; font-size: 14px;">
                    ${videoSlug}
                  </span>
                </div>
                <div style="color: #64748b; font-family: monospace; font-size: 14px; background: #f1f5f9; padding: 8px; border-radius: 4px; text-align: center; direction: ltr;">
                  [<span style="color: #3b82f6; font-weight: bold;">${videoSlug}</span>:<span style="color: #dc2626; font-weight: bold;">${videoTitle}</span>]
                </div>
              </div>

              <div style="display: flex; gap: 16px; font-size: 12px; color: #64748b;">
                <span>🕒 ${videoData.duration || 0} ثانیه</span>
                <span>📊 ${videoData.size || 0} مگابایت</span>
                ${videoImage?.alt ? `<span>📝 ${videoImage.alt}</span>` : ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const viewFragment = editor.data.processor.toView(videoHtml);
    const modelFragment = editor.data.toModel(viewFragment);

    editor.model.change((writer) => {
      editor.model.insertContent(
        modelFragment,
        editor.model.document.selection
      );
    });

    console.log("ویدیو با موفقیت درج شد");
  }, []);

  const handleConfirmVideos = useCallback(() => {
    console.log("ثبت ویدیوها:", tempVideos);

    const validVideos = tempVideos.filter((v) => v.video && v.image);

    if (validVideos.length === 0) {
      console.warn("هیچ ویدیوی معتبری برای ثبت وجود ندارد");
      alert("لطفا برای هر ویدیو هم ویدیو و هم تصویر انتخاب کنید");
      return;
    }

    validVideos.forEach((videoObj, index) => {
      if (videoObj.video && videoObj.image) {
        setTimeout(() => {
          insertVideoAsHtml(videoObj.video, videoObj.image);
        }, index * 100);
      }
    });

    setVideos((prev) => {
      const newVideos = [...prev, ...validVideos];
      console.log("ویدیوهای جدید اضافه شد. کل ویدیوها:", newVideos);
      return newVideos;
    });

    setTempVideos([{ video: null }]);
    setShowVideoModal(false);

    console.log("ویدیوها با موفقیت ثبت شدند");
  }, [tempVideos, insertVideoAsHtml]);

  const handleEditorReady = (editor) => {
    editorRef.current = editor;
    editorInstanceRef.current = editor;

    console.log("ویرایشگر آماده شد");

    if (editor.ui.view.stickyPanel && editor.ui.view.stickyPanel.element) {
      editor.ui.view.stickyPanel.element.style.position = "static";
      editor.ui.view.stickyPanel.element.style.top = "auto";
    }

    editor.editing.view.document.on("click", () => {
      if (editor.ui.view.stickyPanel) {
        editor.ui.view.stickyPanel.element.style.position = "static";
        editor.ui.view.stickyPanel.element.style.top = "auto";
        editor.ui.view.stickyPanel.isSticky = false;
      }
    });

    const toolbarElement = document.querySelector(".ck-toolbar");
    toolbarRef.current = toolbarElement;

    const galleryButton = document.createElement("button");
    galleryButton.type = "button";
    galleryButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M4.502 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/>
        <path d="M14.002 13a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2V5A2 2 0 0 1 2 3a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-1.998 2zM14 2H4a1 1 0 0 0-1 1h9.002a2 2 0 0 1 2 2v7A1 1 0 0 0 15 11V3a1 1 0 0 0-1-1zM2 4a1 1 0 0 0-1 1v8l2.646-2.354a.5.5 0 0 1 .63-.062l2.66 1.773 3.71-3.71a.5.5 0 0 1 .577-.094L15 9.499V5a1 1 0 0 0-1-1H2z"/>
      </svg>
      
    `;
    galleryButton.className = "ck ck-button ck-button_with-text";
    galleryButton.style.marginRight = "5px";
    galleryButton.onclick = async () => {
      console.log("باز کردن گالری برای ادیتور");
      await fetchEditorImages(1);
      setShowGalleryForEditor(true);
    };

    const videoButton = document.createElement("button");
    videoButton.type = "button";
    videoButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
<path fill-rule="evenodd" clip-rule="evenodd" d="M13.334 2C13.6704 1.99989 13.9944 2.12694 14.241 2.35566C14.4877 2.58439 14.6388 2.8979 14.664 3.23333L14.6673 3.33333V12.6667C14.6674 13.0031 14.5404 13.327 14.3117 13.5737C14.0829 13.8204 13.7694 13.9714 13.434 13.9967L13.334 14H2.66732C2.33093 14.0001 2.00694 13.8731 1.76029 13.6443C1.51363 13.4156 1.36255 13.1021 1.33732 12.7667L1.33398 12.6667V3.33333C1.33388 2.99695 1.46092 2.67296 1.68965 2.4263C1.91838 2.17965 2.23188 2.02856 2.56732 2.00333L2.66732 2H13.334ZM13.334 3.33333H2.66732V12.6667H13.334V3.33333ZM6.89398 5.092L7.23932 5.24533L7.46465 5.352L7.72265 5.47867L8.00932 5.624L8.32265 5.79067L8.66065 5.97733L8.83798 6.07867L9.18332 6.282L9.49932 6.47667L9.78598 6.65867L10.0393 6.82733L10.3593 7.04733L10.602 7.22267L10.6653 7.26933C10.7682 7.34589 10.8518 7.44544 10.9093 7.56003C10.9669 7.67463 10.9969 7.80109 10.9969 7.92933C10.9969 8.05758 10.9669 8.18404 10.9093 8.29864C10.8518 8.41323 10.7682 8.51278 10.6653 8.58933L10.45 8.746L10.1567 8.95133L9.91932 9.11067L9.64932 9.286L9.34732 9.47467L9.01465 9.67467L8.65865 9.88067L8.31932 10.0687L8.00532 10.2353L7.71865 10.382L7.46132 10.5073L7.04665 10.6993L6.89332 10.766C6.7756 10.8167 6.64766 10.8392 6.51969 10.8318C6.39173 10.8243 6.26729 10.787 6.15629 10.7229C6.04529 10.6588 5.95081 10.5696 5.88039 10.4625C5.80996 10.3554 5.76553 10.2333 5.75065 10.106L5.71198 9.72933L5.69065 9.48L5.66265 9.03733L5.64732 8.69867L5.63732 8.32867L5.63465 8.13267L5.63332 7.92933C5.63332 7.79156 5.63465 7.65822 5.63732 7.52933L5.64732 7.15933L5.66265 6.82133L5.68065 6.51667L5.70065 6.24933L5.75065 5.75333C5.76544 5.62588 5.80982 5.50364 5.88024 5.39639C5.95067 5.28913 6.04518 5.19981 6.15625 5.13557C6.26732 5.07132 6.39187 5.03393 6.51996 5.02636C6.64804 5.0188 6.77613 5.04127 6.89398 5.092ZM7.30132 6.75933L7.01065 6.614L6.99132 6.93867L6.97665 7.306L6.96798 7.71267L6.96665 7.92933L6.96798 8.146L6.97665 8.552L6.98332 8.74067L7.00065 9.08733L7.01065 9.244L7.29998 9.09867L7.62465 8.928L7.98132 8.732L8.17065 8.624L8.53798 8.40667L8.86732 8.20267L9.15798 8.016L9.28865 7.92933L9.01732 7.74933L8.70665 7.55333C8.52988 7.44389 8.35141 7.33721 8.17132 7.23333L7.98265 7.126L7.62665 6.93L7.30132 6.75933Z" fill="black"/>
</svg>
      
    `;
    videoButton.className = "ck ck-button ck-button_with-text";
    videoButton.style.marginRight = "5px";
    videoButton.onclick = () => {
      console.log("باز کردن مودال ویدیو");
      setShowVideoModal(true);
    };

    if (toolbarElement) {
      const toolbarGroup = document.createElement("div");
      toolbarGroup.className = "ck ck-toolbar__items";
      toolbarGroup.appendChild(galleryButton);
      toolbarGroup.appendChild(videoButton);
      toolbarElement.insertBefore(toolbarGroup, toolbarElement.firstChild);
      setCustomButton(galleryButton);
    }

    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return {
        upload: () => {
          return loader.file
            .then((file) => uploadImageToServer(file))
            .then((url) => {
              return { default: url };
            });
        },
        abort: () => {
          console.log("آپلود تصویر لغو شد");
        },
      };
    };

    editor.editing.view.document.on("paste", (evt, data) => {
      const htmlContent = data.dataTransfer.getData("text/html");
      const plainContent = data.dataTransfer.getData("text/plain");

      if (htmlContent) {
        const sanitized = removeExternalLinksFromPastedContent(htmlContent);
        if (sanitized !== htmlContent) {
          evt.stop();

          editor.model.change((writer) => {
            const viewFragment = editor.data.processor.toView(sanitized);
            const modelFragment = editor.data.toModel(viewFragment);
            editor.model.insertContent(
              modelFragment,
              editor.model.document.selection
            );
          });
        }
      } else if (plainContent) {
        if (
          plainContent.startsWith("http") &&
          !plainContent.includes(OUR_DOMAIN)
        ) {
          evt.stop();
          editor.model.change((writer) => {
            const textNode = writer.createText(plainContent);
            editor.model.insertContent(textNode);
          });
        }
      }
    });
  };

  useEffect(() => {
    return () => {
      if (customButton && customButton.parentNode) {
        customButton.parentNode.removeChild(customButton);
      }
    };
  }, [customButton]);

  const handleImageSelectForEditor = (imageUrl, imageTitle) => {
    console.log("درج تصویر در ادیتور:", imageUrl, imageTitle);

    const editor = editorRef.current;
    if (editor && editor.model) {
      editor.model.change((writer) => {
        const imageElement = writer.createElement("imageBlock", {
          src: imageUrl,
          alt: imageTitle || "تصویر انتخاب شده",
        });
        editor.model.insertContent(imageElement);
        editor.editing.view.focus();
      });
    }
    setShowGalleryForEditor(false);
  };

  const handleImageSelectForVideo = (imageUrl, imageTitle, imageId) => {
    console.log("انتخاب تصویر برای ویدیو:", {
      imageUrl,
      imageTitle,
      imageId,
      currentVideoIndex,
    });

    if (currentVideoIndex !== null) {
      const updated = [...tempVideos];
      if (!updated[currentVideoIndex]) {
        updated[currentVideoIndex] = { video: null };
      }
      updated[currentVideoIndex].image = {
        url: imageUrl,
        alt: imageTitle,
        title: imageTitle,
        id: imageId,
      };
      setTempVideos(updated);
      setShowGalleryForVideo(false);
      setCurrentVideoIndex(null);
    }
  };

  const handleVideoSelect = (videoData) => {
    console.log("ویدیو انتخاب شد:", videoData);

    if (showSelect !== false && tempVideos[showSelect]) {
      const updated = [...tempVideos];

      updated[showSelect] = {
        video: {
          ...videoData,
          title: videoData.title || "بدون عنوان",
          slug: videoData.fullSlug,
        },
      };

      setTempVideos(updated);
      setShowSelect(false);

      console.log("tempVideos آپدیت شد:", updated);
    }
  };

  const renderVideoInfo = (video) => {
    if (!video || !video.video) {
      return "هنوز ویدیویی انتخاب نشده";
    }

    const videoTitle = video.video.title || "ویدیو";
    const hasImage = video.image ? "✅ دارای تصویر" : "❌ بدون تصویر";
    const imageId = video.image?.id ? ` (ID: ${video.image.id})` : "";
    return `✅ ویدیو: ${videoTitle} | ${hasImage}${imageId}`;
  };

  return (
    <>
      <div className="z-[99999999999999]">
        <VideoSelector
          title="انتخاب ویدیو"
          show={showSelect !== false}
          setVideo={handleVideoSelect}
          close={() => setShowSelect(false)}
        />
      </div>

      <div className="editor-container min-h-60 text-right mt-3">
        {label && <label className="block mb-2">{label}</label>}
        <div id="editor" className="mx-auto">
          {isReady ? (
            <CKEditor
              editor={ClassicEditor}
              data={content}
              config={{
                toolbar: {
                  items: [
                    "undo",
                    "redo",
                    "|",
                    "heading",
                    "|",
                    "fontSize",
                    "fontColor",
                    "fontBackgroundColor",
                    "|",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "subscript",
                    "superscript",
                    "highlight",
                    "removeFormat",
                    "|",
                    "alignment",
                    "outdent",
                    "indent",
                    "|",
                    "numberedList",
                    "bulletedList",
                    "todoList",
                    "|",
                    "link",
                    "imageUpload",
                    "htmlEmbed",
                    "|",
                    "blockQuote",
                    "horizontalLine",
                    "specialCharacters",
                    "pageBreak",
                    "|",
                    "sourceEditing",
                  ],
                  shouldNotGroupWhenFull: true,
                },
                image: {
                  toolbar: [
                    "imageTextAlternative",
                    "imageStyle:alignLeft",
                    "imageStyle:alignCenter",
                    "imageStyle:alignRight",
                    "imageStyle:full",
                    "imageStyle:side",
                    "linkImage",
                  ],
                  styles: [
                    "alignLeft",
                    "alignCenter",
                    "alignRight",
                    "full",
                    "side",
                  ],
                },
                table: {
                  contentToolbar: [
                    "tableColumn",
                    "tableRow",
                    "mergeTableCells",
                    "tableProperties",
                    "tableCellProperties",
                  ],
                },
                language: "fa",
                placeholder: "چیزی بنویسید ....",
              }}
              onChange={(event, editor) => {
                const data = editor.getData();
                handleModelChange(data);
              }}
              onReady={handleEditorReady}
            />
          ) : (
            <p>در حال بارگذاری ویرایشگر...</p>
          )}
        </div>
      </div>

      {showGalleryForEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999999999999]">
          <div className="bg-white rounded-lg p-4 w-[95%] sm:w-[90%] max-w-[1000px] max-h-[80vh] overflow-y-auto shadow-lg">
            <div className="sticky top-0 z-[100] border-b bg-white pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl sm:text-2xl font-semibold text-blue-600">
                  گالری تصاویر - درج در ادیتور
                </h3>
                <button
                  onClick={() => {
                    setShowGalleryForEditor(false);
                    setEditorSearchInput("");
                    setEditorPage(1);
                  }}
                  className="text-red-500 hover:text-red-700 text-2xl"
                >
                  × بستن
                </button>
              </div>
              <input
                type="text"
                value={editorSearchInput}
                onChange={handleEditorSearchChange}
                placeholder="جستجو در تصاویر..."
                className="border p-2 rounded w-full"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {editorImages.map((image, index) => {
                const imageElement = (
                  <div
                    key={image.id || index}
                    className="border rounded-lg cursor-pointer hover:shadow-xl transition-all transform hover:scale-105"
                    onClick={() =>
                      handleImageSelectForEditor(image.url, image.title)
                    }
                  >
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-32 object-cover"
                      loading="lazy"
                    />
                    <div className="p-2 text-sm">
                      <div>{image.title}</div>
                      <div className="text-xs text-gray-500">
                        ID: {image.id}
                      </div>
                    </div>
                  </div>
                );

                if (index === editorImages.length - 1) {
                  return (
                    <div key={image.id || index} ref={lastEditorImageRef}>
                      {imageElement}
                    </div>
                  );
                }

                return imageElement;
              })}
            </div>

            {editorLoading && (
              <div className="flex justify-center items-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="mr-3 text-blue-600 font-medium">
                  در حال بارگذاری...
                </span>
              </div>
            )}
            {!editorLoading && editorImages.length === 0 && editorNoResults && (
              <p className="text-center mt-4">تصویری پیدا نشد.</p>
            )}
          </div>
        </div>
      )}

      {showGalleryForVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999999999999]">
          <div className="bg-white rounded-lg p-4 w-[95%] sm:w-[90%] max-w-[1000px] max-h-[80vh] overflow-y-auto shadow-lg">
            <div className="sticky top-0 z-[100] border-b bg-white pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl sm:text-2xl font-semibold text-blue-600">
                  گالری تصاویر - انتخاب برای ویدیو
                </h3>
                <button
                  onClick={() => {
                    setShowGalleryForVideo(false);
                    setVideoSearchInput("");
                    setVideoPage(1);
                    setCurrentVideoIndex(null);
                  }}
                  className="text-red-500 hover:text-red-700 text-2xl"
                >
                  × بستن
                </button>
              </div>
              <input
                type="text"
                value={videoSearchInput}
                onChange={handleVideoSearchChange}
                placeholder="جستجو در تصاویر..."
                className="border p-2 rounded w-full"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {videoImages.map((image, index) => {
                const imageElement = (
                  <div
                    key={image.id || index}
                    className="border rounded-lg cursor-pointer hover:shadow-xl transition-all transform hover:scale-105"
                    onClick={() =>
                      handleImageSelectForVideo(
                        image.url,
                        image.title,
                        image.id
                      )
                    }
                  >
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-32 object-cover"
                      loading="lazy"
                    />
                    <div className="p-2 text-sm">
                      <div>{image.title}</div>
                      <div className="text-xs text-gray-500">
                        ID: {image.id}
                      </div>
                    </div>
                  </div>
                );

                if (index === videoImages.length - 1) {
                  return (
                    <div key={image.id || index} ref={lastVideoImageRef}>
                      {imageElement}
                    </div>
                  );
                }

                return imageElement;
              })}
            </div>

            {videoLoading && (
              <div className="flex justify-center items-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="mr-3 text-blue-600 font-medium">
                  در حال بارگذاری...
                </span>
              </div>
            )}
            {!videoLoading && videoImages.length === 0 && videoNoResults && (
              <p className="text-center mt-4">تصویری پیدا نشد.</p>
            )}
          </div>
        </div>
      )}

      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]">
          <div className="bg-white p-5 rounded-lg w-[400px] max-h-[80vh] overflow-auto">
            <h3 className="text-lg font-bold mb-3">افزودن ویدیو</h3>

            {tempVideos.map((video, index) => (
              <div key={index} className="border p-3 rounded mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-sm">
                    ویدیو شماره {index + 1}
                  </span>

                  {tempVideos.length > 1 && (
                    <button
                      onClick={() =>
                        setTempVideos(tempVideos.filter((_, i) => i !== index))
                      }
                      className="text-red-500 text-xs border px-2 py-1 rounded"
                      type="button"
                    >
                      حذف
                    </button>
                  )}
                </div>

                <div className="bg-gray-100 p-2 rounded mb-2 text-sm">
                  {renderVideoInfo(video)}
                </div>

                <button
                  onClick={() => setShowSelect(index)}
                  className="btn btn-primary w-full mb-2"
                  type="button"
                >
                  {video.video ? "تغییر ویدیو" : "انتخاب ویدیو"}
                </button>

                {video.video && (
                  <button
                    onClick={() => {
                      setCurrentVideoIndex(index);
                      setShowGalleryForVideo(true);
                    }}
                    className="btn btn-outline-secondary w-full mb-2"
                    type="button"
                  >
                    {video.image ? "تغییر تصویر" : "انتخاب تصویر"}
                  </button>
                )}

                {video.image && (
                  <div className="mb-2">
                    <img
                      src={video.image.url}
                      alt={video.image.alt || "تصویر ویدیو"}
                      className="w-full h-32 object-cover rounded border"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      تصویر انتخاب شده برای ویدیو
                    </div>
                  </div>
                )}

                {video.image && (
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      متن جایگزین تصویر (Alt) - عنوان:
                    </label>
                    <input
                      type="text"
                      value={video.image.alt || ""}
                      onChange={(e) => {
                        const updated = [...tempVideos];
                        updated[index].image.alt = e.target.value;
                        setTempVideos(updated);
                      }}
                      placeholder="متن توصیفی برای تصویر (اختیاری)"
                      className="w-full border p-2 rounded text-sm"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      این متن به عنوان title برای بک‌اند ارسال می‌شود
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className="flex flex-col gap-2 mt-4">
              <button
                onClick={handleConfirmVideos}
                className="btn btn-success w-full py-2 px-4 bg-green-500 text-white rounded hover:bg-green-600"
                disabled={!tempVideos.some((v) => v.video && v.image)}
              >
                ثبت ویدیوها
              </button>

              <button
                onClick={() => setTempVideos([...tempVideos, { video: null }])}
                type="button"
                className="btn btn-outline-none w-full py-2 px-4 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
              >
                افزودن ویدیو جدید
              </button>

              <button
                onClick={() => {
                  setShowVideoModal(false);
                  setTempVideos([{ video: null }]);
                }}
                type="button"
                className="btn btn-outline-secondary w-full py-2 px-4 border border-gray-500 text-gray-500 rounded hover:bg-gray-50"
              >
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CkEditorFullNew;
